// BUILD-042 (Oct 17) - one shared, server-backed source for "what needs attention".
//
// Two independent slices, never merged: `me` (the caller's own items, My HR) and `admin` (what HR must act on). Someone who is both an
// employee and an HR administrator therefore sees two separate counts. Every number comes from the server (GET /hr/me/attention and
// GET /hr/admin/attention), which scopes it to the caller's existing authority; nothing is computed from whichever page of employees
// happens to be loaded, and nothing here can be cleared by viewing or dismissing - only by the underlying work changing.
//
// Refresh: on sign-in/ready, on every route change, when the tab regains focus, every 60 seconds, and right after any successful write
// under /hr/ (client.ts calls our listener), so counts follow assign / submit / sign / review / correction / cancel without a reload.
// A failed read leaves the slice in an `error` state (shown as "could not check"), never as zero.
import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { getMyHrAttention, getHrAdminAttention, setHrMutationListener, sessionGeneration } from '../../api/client';
import { idleSlice, type AttentionSlice, type MyAttention, type AdminAttention } from './workspace/attentionModel';

interface Store { me: AttentionSlice<MyAttention>; admin: AttentionSlice<AdminAttention> }

let store: Store = { me: idleSlice<MyAttention>(), admin: idleSlice<AdminAttention>() };
const listeners = new Set<() => void>();
const sequence = { me: 0, admin: 0 };
const enabled = { me: false, admin: false };
let storeGeneration = -1;

function emit(next: Store) {
  store = next;
  listeners.forEach((l) => l());
}

/** A different sign-in must never see the previous person's numbers. */
function resetForNewSession() {
  if (storeGeneration === sessionGeneration) return;
  storeGeneration = sessionGeneration;
  sequence.me++; sequence.admin++;
  emit({ me: idleSlice<MyAttention>(), admin: idleSlice<AdminAttention>() });
}

async function refreshKind(kind: 'me' | 'admin') {
  resetForNewSession();
  if (!enabled[kind]) return;
  const mine = ++sequence[kind];
  const generation = sessionGeneration;
  const prior = store[kind];
  emit({ ...store, [kind]: { status: 'loading', data: prior.data, error: null } } as Store);
  try {
    const data = kind === 'me' ? await getMyHrAttention() : await getHrAdminAttention();
    if (mine !== sequence[kind] || generation !== sessionGeneration) return; // a newer read (or a new session) owns the slice now
    emit({ ...store, [kind]: { status: 'ready', data, error: null } } as Store);
  } catch (e: any) {
    if (mine !== sequence[kind] || generation !== sessionGeneration) return;
    // The last good numbers are kept in `data` but the view layer shows the error state, never a number, while status is 'error'.
    emit({ ...store, [kind]: { status: 'error', data: prior.data, error: e?.message || 'Could not check what needs attention.' } } as Store);
  }
}

/** Re-read everything that is switched on. Safe to call at any time and as often as needed. */
export function refreshAttention() {
  if (enabled.me) void refreshKind('me');
  if (enabled.admin) void refreshKind('admin');
}

/** Switch the two reads on/off to match the caller's resolved surfaces (never an authorization decision - the server re-checks). */
export function configureAttention(next: { me: boolean; admin: boolean }) {
  resetForNewSession();
  const turnedOn = { me: next.me && !enabled.me, admin: next.admin && !enabled.admin };
  enabled.me = next.me; enabled.admin = next.admin;
  if (!next.me && store.me.status !== 'idle') emit({ ...store, me: idleSlice<MyAttention>() });
  if (!next.admin && store.admin.status !== 'idle') emit({ ...store, admin: idleSlice<AdminAttention>() });
  if (turnedOn.me) void refreshKind('me');
  if (turnedOn.admin) void refreshKind('admin');
}

// Any successful write under /hr/ re-reads the counts.
setHrMutationListener(() => refreshAttention());

function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
const snapshot = () => store;

export function useMyAttention(): AttentionSlice<MyAttention> {
  return useSyncExternalStore(subscribe, snapshot).me;
}
export function useAdminAttention(): AttentionSlice<AdminAttention> {
  return useSyncExternalStore(subscribe, snapshot).admin;
}

/**
 * Keep the counts fresh. Mounted ONCE, in the app shell. `surfaces` is what GET /hr/me resolved (null while unresolved/failed);
 * it only decides which reads are attempted, since the server independently scopes every response.
 */
export function useAttentionSync(surfaces: { myHr: boolean; hrAdmin: boolean } | null) {
  const location = useLocation();
  const wantMe = !!surfaces?.myHr;
  const wantAdmin = !!surfaces?.hrAdmin;

  useEffect(() => { configureAttention({ me: wantMe, admin: wantAdmin }); }, [wantMe, wantAdmin]);
  // Navigation is the natural moment the user has "come back" to the numbers.
  useEffect(() => { refreshAttention(); }, [location.pathname, location.search]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshAttention(); };
    const timer = window.setInterval(refreshAttention, 60000);
    window.addEventListener('focus', refreshAttention);
    window.addEventListener('online', refreshAttention);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshAttention);
      window.removeEventListener('online', refreshAttention);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
